import React, { useMemo, useState, useRef, useEffect } from 'react';
import { VIEWS, type SignaView } from '../../lib/view';
import { useEmailStore } from '../../stores/email-store';
import { useTodoStore } from '../../stores/todo-store';
import { useAppStore } from '../../stores/appStore';
import { useAuthStore } from '../../stores/authStore';
import { API_BASE } from '../../lib/api-config';
import { SignaGlyph } from '../pa/SignaGlyph';
import { ThemeToggle } from './ThemeToggle';
import { AccentPicker } from './AccentPicker';

function NavIcon({ id }: { id: SignaView }) {
  const s = { width: 15, height: 15, fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  if (id === 'today') return (
    <svg viewBox="0 0 16 16" {...s}>
      <circle cx="8" cy="8" r="6" />
      <path d="M8 4.5v3.5l2 2" />
    </svg>
  );
  if (id === 'inbox') return (
    <svg viewBox="0 0 16 16" {...s}>
      <path d="M2 10h3l1.5 2h3L11 10h3" />
      <rect x="2" y="4" width="12" height="8" rx="1.5" />
    </svg>
  );
  if (id === 'archive') return (
    <svg viewBox="0 0 16 16" {...s}>
      <rect x="2.5" y="4.5" width="11" height="8.5" rx="1.5" />
      <path d="M4 4.5 5.2 2.8h5.6L12 4.5" />
      <path d="M6 8h4" />
    </svg>
  );
  if (id === 'todos') return (
    <svg viewBox="0 0 16 16" {...s}>
      <rect x="3" y="3" width="10" height="10" rx="1.5" />
      <path d="M5.5 8l2 2 3-3" />
    </svg>
  );
  if (id === 'notes') return (
    <svg viewBox="0 0 16 16" {...s}>
      <rect x="3" y="2" width="10" height="12" rx="1.5" />
      <path d="M5.5 6h5 M5.5 9h3" />
    </svg>
  );
  // memory
  return (
    <svg viewBox="0 0 16 16" {...s}>
      <path d="M8 2c-1.5 0-2.5 1-2.5 2.5 0 .8.3 1.5.8 2-.5.4-.8 1-.8 1.5 0 1.2 1.1 2 2.5 2s2.5-.8 2.5-2c0-.5-.3-1.1-.8-1.5.5-.5.8-1.2.8-2C10.5 3 9.5 2 8 2z" />
      <path d="M5.5 10.5C4 11 3 12 3 13h10c0-1-1-2-2.5-2.5" />
    </svg>
  );
}

/**
 * Left sidebar - brand, top-level nav, PA status card, theme/accent controls.
 * 220px fixed width per the handoff.
 */

interface SidebarProps {
  view: SignaView;
  onViewChange: (v: SignaView) => void;
}

export function Sidebar({ view, onViewChange }: SidebarProps) {
  const emails = useEmailStore((s) => s.emails);
  const todos = useTodoStore((s) => s.todos);
  const setActiveEmailId = useAppStore((s) => s.setActiveEmailId);
  const setActiveTodoId = useAppStore((s) => s.setActiveTodoId);
  const setActiveNoteId = useAppStore((s) => s.setActiveNoteId);
  const { user, token, logout } = useAuthStore();
  const emailConnections = useEmailStore((s) => s.connections);
  const activeProvider = useEmailStore((s) => s.activeProvider);
  const loadEmailConnections = useEmailStore((s) => s.loadConnections);
  const loadEmails = useEmailStore((s) => s.loadEmails);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [connectingProvider, setConnectingProvider] = useState<'gmail' | null>(null);
  const [connectError, setConnectError] = useState<string | null>(null);
  const accountRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user && token) void loadEmailConnections();
  }, [user, token, loadEmailConnections]);

  useEffect(() => {
    if (!user || !token || token === 'demo-token' || !activeProvider) return;

    const hasInboxCache = useEmailStore
      .getState()
      .emails.some((email) => email.provider === activeProvider && !email.isArchived);

    if (!hasInboxCache) {
      void loadEmails(activeProvider, undefined, false);
    }
  }, [user, token, activeProvider, loadEmails]);

  useEffect(() => {
    if (!accountMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) {
        setAccountMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [accountMenuOpen]);

  const handleNavChange = (v: SignaView) => {
    setActiveEmailId(null);
    setActiveTodoId(null);
    setActiveNoteId(null);
    onViewChange(v);
  };

  const handleConnectGmail = async () => {
    if (!token || connectingProvider) return;
    setConnectError(null);
    setConnectingProvider('gmail');
    try {
      const res = await fetch(`${API_BASE}/oauth/gmail`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json() as { url?: string; message?: string; error?: string };
      if (!res.ok || !data.url) {
        throw new Error(data.message ?? data.error ?? 'Could not start Gmail connection');
      }
      window.location.href = data.url;
    } catch (err) {
      setConnectError(err instanceof Error ? err.message : 'Could not start Gmail connection');
      setConnectingProvider(null);
    }
  };

  const unreadCount = useMemo(
    () => emails.filter((e) => !e.isRead && !e.isArchived).length,
    [emails]
  );
  const archivedCount = useMemo(
    () => emails.filter((e) => e.isArchived).length,
    [emails]
  );
  const openTodos = useMemo(
    () => todos.filter((t) => !t.completedAt).length,
    [todos]
  );

  const openThreads = useMemo(
    () => emails.filter((e) => !e.isArchived).length,
    [emails]
  );
  const distinctPeople = useMemo(() => {
    const set = new Set<string>();
    emails.forEach((e) => {
      if (!e.isArchived) set.add(e.fromEmail);
    });
    return set.size;
  }, [emails]);

  const badges: Partial<Record<SignaView, number>> = {
    inbox: unreadCount,
    archive: archivedCount,
    todos: openTodos,
  };

  return (
    <aside
      style={{
        width: 220,
        flexShrink: 0,
        background: 'var(--sidebar-bg)',
        borderRight: '1px solid var(--rule)',
        display: 'flex',
        flexDirection: 'column',
        padding: '18px 14px 16px',
      }}
    >
      {/* Brand */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '4px 8px 22px',
        }}
      >
        <SignaGlyph size={22} />
        <span
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 19,
            letterSpacing: '-0.4px',
            color: 'var(--ink)',
            fontWeight: 600,
          }}
        >
          Signa
        </span>
      </div>

      {/* Nav */}
      <nav style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {VIEWS.map((it) => {
          const active = view === it.id;
          const badge = badges[it.id];
          return (
            <button
              key={it.id}
              onClick={() => handleNavChange(it.id)}
              title={`${it.label} (${it.shortcut})`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 10px',
                fontSize: 13.5,
                fontFamily: 'var(--font-ui)',
                fontWeight: active ? 500 : 400,
                color: active ? 'var(--ink)' : 'var(--ink-soft)',
                background: active ? 'var(--sidebar-active)' : 'transparent',
                border: 'none',
                borderRadius: 7,
                cursor: 'pointer',
                textAlign: 'left',
                width: '100%',
              }}
              onMouseEnter={(e) => {
                if (!active) e.currentTarget.style.background = 'var(--sidebar-hover)';
              }}
              onMouseLeave={(e) => {
                if (!active) e.currentTarget.style.background = 'transparent';
              }}
            >
              <span
                style={{
                  width: 16,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: active ? 'var(--accent)' : 'var(--ink-faint)',
                }}
              >
                <NavIcon id={it.id} />
              </span>
              <span style={{ flex: 1 }}>{it.label}</span>
              {badge !== undefined && badge > 0 && (
                <span
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 10,
                    color: 'var(--ink-faint)',
                    padding: '1px 5px',
                    background: 'var(--rule)',
                    borderRadius: 4,
                  }}
                >
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Theme + accent controls */}
      <div
        style={{
          marginTop: 16,
          padding: '4px 4px 0',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <ThemeToggle />
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }} title="Main color">
          <AccentPicker />
          <span style={{ fontSize: 10, color: 'var(--ink-faint)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Color
          </span>
        </div>
      </div>

      {/* Account chip */}
      {user && (
        <div ref={accountRef} style={{ marginTop: 'auto', position: 'relative' }}>
          <button
            type="button"
            onClick={() => setAccountMenuOpen((v) => !v)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 10px',
              marginBottom: 8,
              background: 'transparent',
              border: '1px solid var(--rule)',
              borderRadius: 8,
              cursor: 'pointer',
              fontFamily: 'var(--font-ui)',
              color: 'var(--ink-soft)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--sidebar-hover)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <span style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--bg-base)', fontSize: 10, fontWeight: 600, flexShrink: 0 }}>
              {(user.email?.[0] ?? '?').toUpperCase()}
            </span>
            <span style={{ fontSize: 11.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, textAlign: 'left' }}>
              {user.email}
            </span>
          </button>
          {accountMenuOpen && (
            <div style={{ position: 'absolute', bottom: '110%', left: 0, right: 0, background: 'var(--sidebar-bg)', border: '1px solid var(--rule)', borderRadius: 8, overflow: 'hidden', boxShadow: 'var(--shadow-md)', zIndex: 50 }}>
              <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--rule)' }}>
                <div style={{ fontSize: 10, color: 'var(--ink-faint)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                  Email accounts
                </div>
                {emailConnections.length > 0 ? (
                  emailConnections.map((connection) => (
                    <div key={connection.connectionId ?? connection.provider} style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 12.5, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {connection.emailAddress}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--ink-faint)' }}>
                        {connection.provider === 'gmail' ? 'Gmail' : 'Microsoft 365'} · {connection.syncStatus ?? 'idle'}
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ fontSize: 12, color: 'var(--ink-soft)', marginBottom: 8 }}>
                    No email account connected.
                  </div>
                )}
                {connectError && (
                  <div style={{ fontSize: 11, color: 'var(--error)', marginBottom: 8 }}>
                    {connectError}
                  </div>
                )}
                <button
                  type="button"
                  onClick={handleConnectGmail}
                  disabled={connectingProvider !== null}
                  style={{ width: '100%', padding: '7px 10px', background: 'var(--paper)', border: '1px solid var(--rule)', borderRadius: 6, textAlign: 'left', fontSize: 12.5, fontFamily: 'var(--font-ui)', color: 'var(--ink)', cursor: connectingProvider ? 'wait' : 'pointer' }}
                >
                  {emailConnections.some((c) => c.provider === 'gmail') ? 'Reconnect Gmail' : 'Connect Gmail'}
                </button>
                <div style={{ marginTop: 6, fontSize: 11, color: 'var(--ink-faint)' }}>
                  Microsoft 365: coming soon
                </div>
              </div>
              <button
                type="button"
                onClick={() => { setAccountMenuOpen(false); logout(); }}
                style={{ width: '100%', padding: '10px 14px', background: 'transparent', border: 'none', textAlign: 'left', fontSize: 13, fontFamily: 'var(--font-ui)', color: 'var(--ink-soft)', cursor: 'pointer' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--sidebar-hover)'; e.currentTarget.style.color = 'var(--ink)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--ink-soft)'; }}
              >
                Sign out
              </button>
            </div>
          )}
        </div>
      )}

      {/* Signa status card */}
      <div
        style={{
          padding: '14px 12px',
          background: 'var(--sidebar-hover)',
          borderRadius: 10,
          border: '1px solid var(--rule)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 8,
          }}
        >
          <SignaGlyph size={14} pulse />
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              letterSpacing: '1.2px',
              textTransform: 'uppercase',
              color: 'var(--ink-faint)',
            }}
          >
            <span>Signa</span>
            <span
              aria-hidden="true"
              style={{
                width: 1,
                height: 10,
                background: 'var(--rule)',
              }}
            />
            <span>Active</span>
          </span>
        </div>
        <div style={{ fontSize: 12, color: 'var(--ink-soft)', lineHeight: 1.5 }}>
          {openThreads === 0 ? (
            <>Nothing open right now.</>
          ) : (
            <>
              Tracking{' '}
              <strong style={{ color: 'var(--ink)', fontWeight: 500 }}>
                {openThreads} open thread{openThreads === 1 ? '' : 's'}
              </strong>
              {distinctPeople > 0 && (
                <>
                  {' '}across {distinctPeople} {distinctPeople === 1 ? 'person' : 'people'}
                </>
              )}
              .
            </>
          )}
        </div>
      </div>
    </aside>
  );
}
